import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Box,
  ButtonProps,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { GridAddIcon } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { COLORS } from "../../assets/theme";
import Actions from "../../components/common/Actions";
import { IAutoCompleteProps } from "../../components/common/AutoComplete";
import { ICustomButtonProps } from "../../components/common/CustomButton";
import CustomDialog, { FieldType } from "../../components/common/CustomDialog";
import CustomLabel from "../../components/common/CustomLabel";
import CustomTextField from "../../components/common/CustomTextField";
import SearchForm from "../../components/common/SearchForm";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import History from "../../components/History";
import ToastNotification from "../../components/ToastNotification";
import {
  addVpnTunnelApi,
  connectVpnTunnelApi,
  deleteVpnTunnelApi,
  disconnectVpnTunnelApi,
  editVpnTunnelApi,
  fetchVpnTunnelDiagnosticsApi,
  fetchVpnTunnelHistory,
  fetchVpnTunnelStatusesApi,
  getVpnTunnelById,
  IVpnTunnel,
  IVpnTunnelDiagnostics,
  IVpnTunnelStatusResponse,
  useVpnTunnelsQuery,
} from "../../services/VpnTunnelService";
import { IField, Params } from "../../types/table/interfaces.type";
import { hasJwtPermission } from "../../services/ServicesHelpers";

interface VpnTunnelDialogData extends Partial<IVpnTunnel> {
  changePsk?: boolean;
}

type TunnelConnectionAction = "connect" | "disconnect";

const getStatusChipColor = (
  status: string,
): "success" | "error" | "warning" | "default" => {
  switch (status) {
    case "UP":
      return "success";
    case "CONNECTING":
      return "warning";
    case "DOWN":
      return "error";
    default:
      return "default";
  }
};

const isBlank = (value: unknown): boolean =>
  value === undefined || value === null || String(value).trim() === "";

const CONNECTION_NAME_REGEX = /^[A-Za-z0-9._-]+$/;

const isValidConnectionName = (value: unknown): boolean =>
  CONNECTION_NAME_REGEX.test(String(value ?? "").trim());

const VpnTunnelPage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [filters, setFilters] = useState<Params<IVpnTunnel>>({});
  const [appliedFilters, setAppliedFilters] = useState<Params<IVpnTunnel>>({});
  const [statusByConnectionName, setStatusByConnectionName] = useState<
    Record<string, IVpnTunnelStatusResponse>
  >({});
  const [connectionConfirmOpen, setConnectionConfirmOpen] = useState(false);
  const [pendingConnectionAction, setPendingConnectionAction] = useState<{
    id: number;
    connectionName: string;
    action: TunnelConnectionAction;
  } | null>(null);

  const dialogRef = useRef<VpnTunnelDialogRef>(null);
  const diagnosticsDialogRef = useRef<VpnDiagnosticsDialogRef>(null);

  const { data: tunnelsData } = useVpnTunnelsQuery(appliedFilters);

  const refreshStatuses = useCallback(async () => {
    try {
      const statuses = await fetchVpnTunnelStatusesApi();
      const statusMap = statuses.reduce(
        (acc, item) => {
          acc[item.connectionName] = item;
          return acc;
        },
        {} as Record<string, IVpnTunnelStatusResponse>,
      );
      setStatusByConnectionName(statusMap);
    } catch (error: any) {
      ToastNotification.error(error.message);
    }
  }, []);

  useEffect(() => {
    refreshStatuses();
    const interval = setInterval(() => {
      refreshStatuses();
    }, 15000);
    return () => clearInterval(interval);
  }, [refreshStatuses]);

  const handleOpenDialog = (
    mode: "add" | "edit" | "delete" | "history",
    id?: string,
  ) => {
    dialogRef.current?.openDialog(mode, id);
  };

  const openConnectionConfirmDialog = (id: string | number) => {
    const row = tunnelsData?.content?.find((item) => item.id === Number(id));
    if (!row) return;
    const currentStatus =
      statusByConnectionName[row.connectionName]?.status ?? "DOWN";
    const action: TunnelConnectionAction =
      currentStatus === "UP" ? "disconnect" : "connect";
    setPendingConnectionAction({
      id: Number(id),
      connectionName: row.connectionName,
      action,
    });
    setConnectionConfirmOpen(true);
  };

  const handleToggleTunnelConnection = async () => {
    try {
      if (!pendingConnectionAction) return;
      if (pendingConnectionAction.action === "disconnect") {
        await disconnectVpnTunnelApi(pendingConnectionAction.id);
        ToastNotification.success("VPN tunnel disconnected.");
      } else {
        await connectVpnTunnelApi(pendingConnectionAction.id);
        ToastNotification.success("VPN tunnel connect requested.");
      }
      await refreshStatuses();
      setConnectionConfirmOpen(false);
      setPendingConnectionAction(null);
    } catch (error: any) {
      ToastNotification.error(error.message);
    }
  };

  const actions = Actions({
    handleOpenEditModal: {
      handler: async (id: string | number) => {
        handleOpenDialog("edit", String(id));
      },
      isDisabled: !hasJwtPermission("GET_VPN_TUNNEL"),
    },
    onDelete: {
      handler: async (id: string | number) => {
        handleOpenDialog("delete", String(id));
      },
      isDisabled: !hasJwtPermission("DELETE_VPN_TUNNEL"),
    },
    onViewHistory: {
      handler: async (id: string | number) => {
        handleOpenDialog("history", String(id));
      },
      isDisabled: !hasJwtPermission("GET_ALL_VPN_TUNNELS_HISTORY"),
    },
    onPlay: {
      handler: async (id: string | number) => {
        openConnectionConfirmDialog(id);
      },
      isDisabled: (row: IVpnTunnel) => {
        const currentStatus =
          statusByConnectionName[row.connectionName]?.status ?? "DOWN";
        // If currently UP, action is disconnect, else connect
        const requiredPermission =
          currentStatus === "UP"
            ? "DISCONNECT_VPN_TUNNEL"
            : "CONNECT_VPN_TUNNEL";
        return !hasJwtPermission(requiredPermission);
      },
      icon: (row: IVpnTunnel) => {
        const currentStatus =
          statusByConnectionName[row.connectionName]?.status ?? "DOWN";
        const isUp = currentStatus === "UP";
        return (
          <Tooltip
            title={isUp ? "Disconnect VPN tunnel" : "Connect VPN tunnel"}
          >
            <Box sx={{ display: "inline-flex" }}>
              {isUp ? (
                <LinkOffIcon
                  sx={{
                    width: 23,
                    color: hasJwtPermission("DISCONNECT_VPN_TUNNEL")
                      ? COLORS.GOLD
                      : "#D3D3D3",
                  }}
                />
              ) : (
                <LinkIcon
                  sx={{
                    width: 23,
                    color: hasJwtPermission("CONNECT_VPN_TUNNEL")
                      ? COLORS.GOLD
                      : "#D3D3D3",
                  }}
                />
              )}
            </Box>
          </Tooltip>
        );
      },
    },
    onDiagnostics: {
      handler: (id: string | number) => {
        const row = tunnelsData?.content?.find(
          (item) => item.id === Number(id),
        );
        if (!row) return;
        diagnosticsDialogRef.current?.open(row.id, row.connectionName);
      },
      isDisabled: !hasJwtPermission("ROLE_CONNECT_VPN_TUNNEL"),
    },
  });

  const fields: IField<IVpnTunnel>[] = [
    { key: "connectionName", header: "Connection Name" },
    { key: "remoteGateway", header: "Remote Gateway" },
    { key: "remoteSubnets", header: "Remote Subnets" },
    { key: "protocol", header: "Protocol" },
    {
      key: "connectionName",
      header: "Status",
      transform: (row) => {
        const liveStatus = statusByConnectionName[row.connectionName];
        const currentStatus = liveStatus?.status ?? "DOWN";
        return (
          <Tooltip
            title={
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 0,
                  fontSize: "12px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  maxWidth: "420px",
                }}
              >
                {liveStatus
                  ? JSON.stringify(liveStatus, null, 2)
                  : "No fresh /statuses data yet for this tunnel."}
              </Box>
            }
            arrow
          >
            <Chip
              label={currentStatus}
              color={getStatusChipColor(currentStatus)}
              size="small"
              clickable={false}
              onClick={(e) => e.stopPropagation()}
            />
          </Tooltip>
        );
      },
    },
    {
      key: "connectionName",
      header: "Connected Since",
      transform: (row) =>
        statusByConnectionName[row.connectionName]?.connectedSince || "-",
    },
    { key: "createdBy", header: "Created By" },
  ];

  const nameOptions =
    tunnelsData?.content?.map((tunnel) => ({
      label: tunnel.connectionName,
      value: tunnel.connectionName,
    })) || [];

  const gatewayOptions =
    tunnelsData?.content?.map((tunnel) => ({
      label: tunnel.remoteGateway,
      value: tunnel.remoteGateway,
    })) || [];

  const autoCompletes: IAutoCompleteProps[] = [
    {
      label: "Connection Name",
      value: filters.connectionName ?? "",
      options: nameOptions,
      clearable: true,
      placeholder: "Select connection name",
      onChange: (value: string | number | (string | number)[]) => {
        setFilters((prev) => ({
          ...prev,
          connectionName: String(value || ""),
        }));
      },
    },
    {
      label: "Remote Gateway",
      value: filters.remoteGateway ?? "",
      options: gatewayOptions,
      clearable: true,
      placeholder: "Select remote gateway",
      onChange: (value: string | number | (string | number)[]) => {
        setFilters((prev) => ({
          ...prev,
          remoteGateway: String(value || ""),
        }));
      },
    },
  ];

  const handleSearchClick = () => {
    setAppliedFilters({ ...filters });
  };

  const buttons = [
    {
      label: "Search",
      variant: "contained" as ButtonProps["variant"],
      bgColor: COLORS.GOLD,
      onClick: handleSearchClick,
      permission: "GET_ALL_VPN_TUNNELS",
    },
  ];

  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <CustomLabel
        text="VPN Tunnels"
        variant={isSmallScreen ? "h6" : "h5"}
        fontWeight={500}
        marginTop={1}
        marginBottom={1}
      />
      <Box sx={{ mb: 3 }}>
        <SearchForm autoCompletes={autoCompletes} buttons={buttons} />
      </Box>

      <Box sx={{ mb: 3 }}>
        <CustomLabel
          text="VPN Tunnel List"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          marginTop={1}
          marginBottom={1}
        />
        <SearchHeaderContainer
          search={appliedFilters.search ?? ""}
          setSearch={(value) =>
            setAppliedFilters((prev) => ({ ...prev, search: value }))
          }
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <GridAddIcon />,
              onClick: () => handleOpenDialog("add"),
              textColor: COLORS.iconColor,
              permission: "POST_VPN_TUNNEL",
            },
          ]}
        />
        <CustomTable<IVpnTunnel>
          filters={appliedFilters}
          fields={fields}
          fetchFunc={useVpnTunnelsQuery}
          actions={actions}
          sortableFields={[
            "connectionName",
            "remoteGateway",
            "protocol",
            "createdDate",
          ]}
        />
      </Box>

      <VpnTunnelDialog
        ref={dialogRef}
        appliedFilters={appliedFilters}
        onRefresh={() => {
          setAppliedFilters((prev) => ({ ...prev }));
          queryClient.invalidateQueries({ queryKey: ["vpn-tunnels"] });
          queryClient.invalidateQueries({ queryKey: ["vpn-tunnels-all"] });
          refreshStatuses();
        }}
      />
      <VpnDiagnosticsDialog ref={diagnosticsDialogRef} />
      <CustomDialog
        open={connectionConfirmOpen}
        onClose={() => {
          setConnectionConfirmOpen(false);
          setPendingConnectionAction(null);
        }}
        mode="confirm"
        title={
          pendingConnectionAction?.action === "disconnect"
            ? "Disconnect VPN Tunnel"
            : "Connect VPN Tunnel"
        }
        fields={[
          {
            type: "custom",
            component: (
              <Box>
                {pendingConnectionAction?.action === "disconnect"
                  ? `Are you sure you want to disconnect "${pendingConnectionAction?.connectionName}"?`
                  : `Are you sure you want to connect "${pendingConnectionAction?.connectionName}"?`}
              </Box>
            ),
          },
        ]}
        buttons={[
          {
            label:
              pendingConnectionAction?.action === "disconnect"
                ? "Disconnect"
                : "Connect",
            onClick: handleToggleTunnelConnection,
            bgColor: COLORS.GOLD,
            permission:
              pendingConnectionAction?.action === "disconnect"
                ? "DISCONNECT_VPN_TUNNEL"
                : "CONNECT_VPN_TUNNEL",
          },
        ]}
      />
    </Box>
  );
};

export interface VpnTunnelDialogRef {
  openDialog: (
    mode: "add" | "edit" | "delete" | "history",
    id?: string,
  ) => void;
}

interface VpnTunnelDialogProps {
  onRefresh: () => void;
  appliedFilters: Params<IVpnTunnel>;
}

const VpnTunnelDialog = forwardRef<VpnTunnelDialogRef, VpnTunnelDialogProps>(
  ({ onRefresh, appliedFilters }, ref) => {
    const initialDialogData: VpnTunnelDialogData = {
      protocol: "IPSEC_IKE",
      pfsEnabled: false,
      isInitiator: true,
      ikeLifetimeSeconds: 86400,
      saLifetimeSeconds: 28800,
      changePsk: false,
    };

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<
      "add" | "edit" | "delete" | "history"
    >("add");
    const [dialogData, setDialogData] =
      useState<VpnTunnelDialogData>(initialDialogData);
    const [selectedId, setSelectedId] = useState<number | undefined>();
    const [showPsk, setShowPsk] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [disconnectConfirmOpen, setDisconnectConfirmOpen] = useState(false);

    useImperativeHandle(ref, () => ({
      openDialog: async (mode, id) => {
        setDialogMode(mode);
        setSubmitted(false);
        setShowPsk(false);
        try {
          if (
            (mode === "edit" || mode === "delete" || mode === "history") &&
            id
          ) {
            const tunnel = await getVpnTunnelById(id);
            setDialogData({
              ...initialDialogData,
              ...tunnel,
              psk: "",
              changePsk: false,
            });
            setSelectedId(Number(id));
          } else {
            setDialogData(initialDialogData);
            setSelectedId(undefined);
          }
          setDialogOpen(true);
        } catch (error: any) {
          ToastNotification.error(error.message);
        }
      },
    }));

    const requiredStringFields = [
      "connectionName",
      "remoteGateway",
      "remoteSubnets",
      "ikeProposal",
      "espProposal",
    ] as const;

    const requiredNumberFields = [
      "ikeLifetimeSeconds",
      "saLifetimeSeconds",
    ] as const;

    const hasLocalNatIp = !isBlank(dialogData.localNatIp);

    const hasInvalidConnectionName =
      !isBlank(dialogData.connectionName) &&
      !isValidConnectionName(dialogData.connectionName);

    const hasRequiredValidationErrors = () => {
      const hasMissingRequiredStrings = requiredStringFields.some((field) =>
        isBlank(dialogData[field]),
      );
      const hasMissingRequiredNumbers = requiredNumberFields.some((field) => {
        const value = Number(dialogData[field]);
        return Number.isNaN(value) || value <= 0;
      });

      const needsPsk =
        dialogMode === "add" || (dialogMode === "edit" && dialogData.changePsk);
      const hasMissingPsk = needsPsk ? isBlank(dialogData.psk) : false;

      return (
        hasMissingRequiredStrings ||
        hasMissingRequiredNumbers ||
        hasMissingPsk ||
        hasInvalidConnectionName
      );
    };

    const handleSave = async () => {
      setSubmitted(true);
      if (hasInvalidConnectionName) {
        ToastNotification.error(
          "Connection Name cannot contain spaces. Use letters, numbers, dot, dash, or underscore only.",
        );
        return;
      }
      if (hasRequiredValidationErrors()) {
        ToastNotification.error("Please fill all required VPN tunnel fields.");
        return;
      }

      try {
        const payload: Partial<IVpnTunnel> = {
          version: dialogData.version,
          connectionName: String(dialogData.connectionName || "").trim(),
          protocol:
            (dialogData.protocol as IVpnTunnel["protocol"]) || "IPSEC_IKE",
          remoteGateway: String(dialogData.remoteGateway || "").trim(),
          remoteSubnets: String(dialogData.remoteSubnets || "").trim(),
          // When Local NAT IP is set it becomes the local selector, so
          // Local Subnets is ignored by the server and not sent.
          localSubnets: hasLocalNatIp
            ? undefined
            : String(dialogData.localSubnets || "").trim() || undefined,
          localNatIp: String(dialogData.localNatIp || "").trim() || undefined,
          localId: String(dialogData.localId || "").trim() || undefined,
          remoteId: String(dialogData.remoteId || "").trim() || undefined,
          ikeProposal: String(dialogData.ikeProposal || "").trim(),
          ikeLifetimeSeconds: Number(dialogData.ikeLifetimeSeconds),
          espProposal: String(dialogData.espProposal || "").trim(),
          saLifetimeSeconds: Number(dialogData.saLifetimeSeconds),
          saLifetimeKbytes: isBlank(dialogData.saLifetimeKbytes)
            ? undefined
            : Number(dialogData.saLifetimeKbytes),
          pfsEnabled: Boolean(dialogData.pfsEnabled),
          isInitiator:
            dialogData.isInitiator !== undefined
              ? Boolean(dialogData.isInitiator)
              : true,
          description: String(dialogData.description || "").trim() || undefined,
        };

        if (dialogMode === "add") {
          payload.psk = String(dialogData.psk || "");
          await addVpnTunnelApi(payload);
          ToastNotification.success("VPN tunnel created successfully.");
        } else if (dialogMode === "edit" && selectedId) {
          if (dialogData.changePsk) {
            payload.psk = String(dialogData.psk || "");
          }
          await editVpnTunnelApi(selectedId, payload);
          ToastNotification.success("VPN tunnel updated successfully.");
        }

        setDialogOpen(false);
        setDialogData(initialDialogData);
        onRefresh();
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    const handleDelete = async () => {
      try {
        if (!selectedId) return;
        await deleteVpnTunnelApi(selectedId);
        ToastNotification.success("VPN tunnel deleted successfully.");
        setDialogOpen(false);
        onRefresh();
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    const handleDisconnectFromEdit = async () => {
      try {
        if (!selectedId) return;
        await disconnectVpnTunnelApi(selectedId);
        ToastNotification.success("VPN tunnel disconnected.");
        setDisconnectConfirmOpen(false);
        onRefresh();
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    const fieldHistory: IField<IVpnTunnel>[] = [
      { key: "connectionName", header: "Connection Name" },
      { key: "protocol", header: "Protocol" },
      { key: "remoteGateway", header: "Remote Gateway" },
      { key: "remoteSubnets", header: "Remote Subnets" },
      { key: "localSubnets", header: "Local Subnets" },
      { key: "localNatIp", header: "Local NAT IP" },
      { key: "localId", header: "Local ID" },
      { key: "remoteId", header: "Remote ID" },
      { key: "ikeProposal", header: "IKE Proposal" },
      { key: "ikeLifetimeSeconds", header: "IKE Lifetime Seconds" },
      { key: "espProposal", header: "ESP Proposal" },
      { key: "saLifetimeSeconds", header: "SA Lifetime Seconds" },
      { key: "saLifetimeKbytes", header: "SA Lifetime Kbytes" },
      {
        key: "pfsEnabled",
        header: "PFS Enabled",
        transform: (row) => (row.pfsEnabled ? "True" : "False"),
      },
      { key: "description", header: "Description" },
      { key: "historyTimestamp", header: "Date Modified" },
      { key: "createdBy", header: "Created By" },
      { key: "updatedBy", header: "Updated By" },
    ];

    const fields: FieldType[] =
      dialogMode === "history"
        ? [
            {
              type: "custom",
              component: (
                <History
                  filters={appliedFilters}
                  fields={fieldHistory}
                  fetchFunc={({ size = 10 }) =>
                    fetchVpnTunnelHistory(Number(selectedId), {
                      size,
                    })
                  }
                />
              ),
            },
          ]
        : dialogMode === "delete"
          ? [
              {
                type: "custom",
                component: (
                  <Box>
                    This will disconnect the tunnel and remove it. Are you sure
                    you want to continue?
                  </Box>
                ),
              },
            ]
          : [
              {
                type: "text",
                label: "Connection Name",
                placeholder: "e.g. vendor-abc",
                value: dialogData.connectionName || "",
                bgColor: COLORS.WhiteBackground,
                highlightLabelStar: true,
                tooltip:
                  "Unique VPN connection identifier. No spaces. Letters, numbers, dot, dash and underscore only.",
                error:
                  (submitted && isBlank(dialogData.connectionName)) ||
                  hasInvalidConnectionName,
                helperText:
                  submitted && isBlank(dialogData.connectionName)
                    ? "Connection Name is required."
                    : hasInvalidConnectionName
                      ? "No spaces allowed. Use letters, numbers, dot, dash, or underscore only."
                      : "",
                onChange: (value: string | number) =>
                  setDialogData((prev) => ({
                    ...prev,
                    connectionName: String(value),
                  })),
              },
              {
                type: "select",
                label: "Protocol",
                options: [{ label: "IPSEC_IKE", value: "IPSEC_IKE" }],
                value: dialogData.protocol || "IPSEC_IKE",
                bgColor: COLORS.WhiteBackground,
                highlightLabelStar: true,
                onChange: (value) =>
                  setDialogData((prev) => ({
                    ...prev,
                    protocol: String(value) as IVpnTunnel["protocol"],
                  })),
              },
              {
                type: "text",
                label: "Remote Gateway",
                placeholder: "Peer IP Address",
                value: dialogData.remoteGateway || "",
                bgColor: COLORS.WhiteBackground,
                highlightLabelStar: true,
                tooltip: "Vendor document: VPN - Peer IP Address",
                error: submitted && isBlank(dialogData.remoteGateway),
                helperText:
                  submitted && isBlank(dialogData.remoteGateway)
                    ? "Remote Gateway is required."
                    : "",
                onChange: (value: string | number) =>
                  setDialogData((prev) => ({
                    ...prev,
                    remoteGateway: String(value),
                  })),
              },
              {
                type: "text",
                label: "Remote Subnets",
                placeholder: "Remote Subnets(Primary host). Comma separated",
                value: dialogData.remoteSubnets || "",
                bgColor: COLORS.WhiteBackground,
                highlightLabelStar: true,
                tooltip: "Vendor document: VPN Access Permission IPs",
                error: submitted && isBlank(dialogData.remoteSubnets),
                helperText:
                  submitted && isBlank(dialogData.remoteSubnets)
                    ? "Remote Subnets is required."
                    : "",
                onChange: (value: string | number) =>
                  setDialogData((prev) => ({
                    ...prev,
                    remoteSubnets: String(value),
                  })),
              },
              {
                type: "text",
                label: "Local Subnets",
                placeholder: hasLocalNatIp
                  ? "Ignored when Local NAT IP is set"
                  : "Leave empty to use server default",
                value: hasLocalNatIp ? "" : dialogData.localSubnets || "",
                bgColor: COLORS.WhiteBackground,
                disabled: hasLocalNatIp,
                tooltip: hasLocalNatIp
                  ? "Ignored while Local NAT IP is set. Local NAT IP becomes the local selector."
                  : undefined,
                onChange: (value: string | number) =>
                  setDialogData((prev) => ({
                    ...prev,
                    localSubnets: String(value),
                  })),
              },
              {
                type: "text",
                label: "Local NAT IP",
                placeholder: "e.g. 10.10.10.5",
                value: dialogData.localNatIp || "",
                bgColor: COLORS.WhiteBackground,
                tooltip:
                  "Leave empty unless the peer requires a public/non-overlapping IP; use a distinct value per tunnel.",
                onChange: (value: string | number) =>
                  setDialogData((prev) => ({
                    ...prev,
                    localNatIp: String(value),
                  })),
              },
              {
                type: "text",
                label: "Local ID",
                placeholder: "Optional local IKE identity",
                value: dialogData.localId || "",
                bgColor: COLORS.WhiteBackground,
                onChange: (value: string | number) =>
                  setDialogData((prev) => ({
                    ...prev,
                    localId: String(value),
                  })),
              },
              {
                type: "text",
                label: "Remote ID",
                placeholder: "Optional remote IKE identity",
                value: dialogData.remoteId || "",
                bgColor: COLORS.WhiteBackground,
                onChange: (value: string | number) =>
                  setDialogData((prev) => ({
                    ...prev,
                    remoteId: String(value),
                  })),
              },
              {
                type: "text",
                label: "IKE Proposal",
                placeholder: "e.g. aes256-sha2_512;dh19",
                value: dialogData.ikeProposal || "",
                bgColor: COLORS.WhiteBackground,
                highlightLabelStar: true,
                tooltip:
                  "Vendor document: Phase I Encryption + Hash + DH Group",
                error: submitted && isBlank(dialogData.ikeProposal),
                helperText:
                  submitted && isBlank(dialogData.ikeProposal)
                    ? "IKE Proposal is required."
                    : "",
                onChange: (value: string | number) =>
                  setDialogData((prev) => ({
                    ...prev,
                    ikeProposal: String(value),
                  })),
              },
              {
                type: "text",
                label: "IKE Lifetime Seconds",
                placeholder: "e.g. 86400",
                typeinput: "number",
                value: dialogData.ikeLifetimeSeconds ?? "",
                bgColor: COLORS.WhiteBackground,
                highlightLabelStar: true,
                tooltip: "Vendor document: Phase I Key lifetime (seconds)",
                error:
                  submitted &&
                  (isBlank(dialogData.ikeLifetimeSeconds) ||
                    Number(dialogData.ikeLifetimeSeconds) <= 0),
                helperText:
                  submitted &&
                  (isBlank(dialogData.ikeLifetimeSeconds) ||
                    Number(dialogData.ikeLifetimeSeconds) <= 0)
                    ? "IKE Lifetime Seconds must be greater than 0."
                    : "",
                onChange: (value: string | number) =>
                  setDialogData((prev) => ({
                    ...prev,
                    ikeLifetimeSeconds: Number(value),
                  })),
              },
              {
                type: "text",
                label: "ESP Proposal",
                placeholder: "e.g. aes256-sha2_512",
                value: dialogData.espProposal || "",
                bgColor: COLORS.WhiteBackground,
                highlightLabelStar: true,
                tooltip: "Vendor document: Phase II Encryption + Auth",
                error: submitted && isBlank(dialogData.espProposal),
                helperText:
                  submitted && isBlank(dialogData.espProposal)
                    ? "ESP Proposal is required."
                    : "",
                onChange: (value: string | number) =>
                  setDialogData((prev) => ({
                    ...prev,
                    espProposal: String(value),
                  })),
              },
              {
                type: "text",
                label: "SA Lifetime Seconds",
                placeholder: "e.g. 28800",
                typeinput: "number",
                value: dialogData.saLifetimeSeconds ?? "",
                bgColor: COLORS.WhiteBackground,
                highlightLabelStar: true,
                tooltip: "Vendor document: Phase II SA duration (seconds)",
                error:
                  submitted &&
                  (isBlank(dialogData.saLifetimeSeconds) ||
                    Number(dialogData.saLifetimeSeconds) <= 0),
                helperText:
                  submitted &&
                  (isBlank(dialogData.saLifetimeSeconds) ||
                    Number(dialogData.saLifetimeSeconds) <= 0)
                    ? "SA Lifetime Seconds must be greater than 0."
                    : "",
                onChange: (value: string | number) =>
                  setDialogData((prev) => ({
                    ...prev,
                    saLifetimeSeconds: Number(value),
                  })),
              },
              {
                type: "text",
                label: "SA Lifetime Kbytes",
                placeholder: "Optional",
                typeinput: "number",
                value: dialogData.saLifetimeKbytes ?? "",
                bgColor: COLORS.WhiteBackground,
                tooltip: "Vendor document: Phase II SA duration (KBytes)",
                onChange: (value: string | number) =>
                  setDialogData((prev) => ({
                    ...prev,
                    saLifetimeKbytes: isBlank(value)
                      ? undefined
                      : Number(value),
                  })),
              },
              {
                type: "select",
                label: "PFS Enabled",
                options: [
                  { label: "False", value: "false" },
                  { label: "True", value: "true" },
                ],
                value: String(dialogData.pfsEnabled ?? false),
                bgColor: COLORS.WhiteBackground,
                highlightLabelStar: true,
                onChange: (value) =>
                  setDialogData((prev) => ({
                    ...prev,
                    pfsEnabled: String(value) === "true",
                  })),
              },
              ...(dialogMode === "edit"
                ? [
                    {
                      type: "switch" as const,
                      label: "Change PSK",
                      value: Boolean(dialogData.changePsk),
                      onChange: (value: boolean) =>
                        setDialogData((prev) => ({
                          ...prev,
                          changePsk: value,
                          psk: value ? prev.psk : "",
                        })),
                    },
                  ]
                : []),
              ...(dialogMode === "add" || dialogData.changePsk
                ? [
                    {
                      type: "custom" as const,
                      fullWidth: true,
                      component: (
                        <CustomTextField
                          type={showPsk ? "text" : "password"}
                          label="Pre-Shared Key (PSK)"
                          placeholder="Enter PSK"
                          value={dialogData.psk || ""}
                          bgColor={COLORS.WhiteBackground}
                          highlightLabelStar={
                            dialogMode === "add" ||
                            Boolean(dialogData.changePsk)
                          }
                          error={submitted && isBlank(dialogData.psk)}
                          helperText={
                            submitted && isBlank(dialogData.psk)
                              ? "PSK is required when creating or changing PSK."
                              : ""
                          }
                          onChange={(event) =>
                            setDialogData((prev) => ({
                              ...prev,
                              psk: event.target.value,
                            }))
                          }
                          endIcon={
                            <IconButton
                              aria-label="toggle psk visibility"
                              onClick={() => setShowPsk((prev) => !prev)}
                              edge="end"
                              size="small"
                            >
                              {showPsk ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          }
                        />
                      ),
                    },
                  ]
                : []),
              {
                type: "text",
                label: "Description",
                placeholder: "Optional notes",
                value: dialogData.description || "",
                bgColor: COLORS.WhiteBackground,
                multiline: true,
                rows: 3,
                onChange: (value: string | number) =>
                  setDialogData((prev) => ({
                    ...prev,
                    description: String(value),
                  })),
              },
              {
                type: "switch" as const,
                label: "Is Initiator",
                value: Boolean(dialogData.isInitiator ?? true),
                onChange: (value: boolean) =>
                  setDialogData((prev) => ({ ...prev, isInitiator: value })),
              },
            ];

    const dialogButtons: ICustomButtonProps[] =
      dialogMode === "history"
        ? []
        : dialogMode === "delete"
          ? [
              {
                label: "Delete",
                onClick: handleDelete,
                variant: "contained",
                bgColor: COLORS.GOLD,
                permission: "DELETE_VPN_TUNNEL",
              },
            ]
          : [
              ...(dialogMode === "edit"
                ? [
                    {
                      label: "Disconnect",
                      onClick: () => setDisconnectConfirmOpen(true),
                      bgColor: "#D32F2F",
                      textColor: "#FFFFFF",
                      disabled:
                        !selectedId ||
                        !hasJwtPermission("DISCONNECT_VPN_TUNNEL"),
                    },
                  ]
                : []),
              {
                label: "Save",
                onClick: handleSave,
                bgColor: COLORS.GOLD,
                disabled:
                  hasRequiredValidationErrors() ||
                  (dialogMode === "edit"
                    ? !hasJwtPermission("PUT_VPN_TUNNEL")
                    : !hasJwtPermission("POST_VPN_TUNNEL")),
              },
            ];

    return (
      <>
        <CustomDialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setDialogData(initialDialogData);
            setSubmitted(false);
            setShowPsk(false);
            setDisconnectConfirmOpen(false);
          }}
          mode={dialogMode}
          title={
            dialogMode === "add"
              ? "Add VPN Tunnel"
              : dialogMode === "edit"
                ? "Edit VPN Tunnel"
                : dialogMode === "history"
                  ? "History"
                  : "Delete VPN Tunnel"
          }
          fields={fields}
          fieldsPerRow={dialogMode === "delete" ? 1 : 2}
          buttons={dialogButtons}
        />
        <CustomDialog
          open={disconnectConfirmOpen}
          onClose={() => setDisconnectConfirmOpen(false)}
          mode="confirm"
          title="Disconnect VPN Tunnel"
          fields={[
            {
              type: "custom",
              component: (
                <Box>
                  Are you sure you want to disconnect "
                  {dialogData.connectionName}"?
                </Box>
              ),
            },
          ]}
          buttons={[
            {
              label: "Disconnect",
              onClick: handleDisconnectFromEdit,
              bgColor: "#D32F2F",
              textColor: "#FFFFFF",
              disabled:
                !selectedId || !hasJwtPermission("DISCONNECT_VPN_TUNNEL"),
            },
          ]}
        />
      </>
    );
  },
);

interface VpnDiagnosticsDialogRef {
  open: (id: number, connectionName: string) => void;
}

interface DiagnosticsPanelProps {
  label: string;
  value: string;
}

const copyToClipboard = async (text: string) => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.style.position = "fixed";
  textArea.style.left = "-9999px";
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  document.execCommand("copy");
  document.body.removeChild(textArea);
};

const DiagnosticsPanel: React.FC<DiagnosticsPanelProps> = ({
  label,
  value,
}) => {
  const [copyLabel, setCopyLabel] = useState("Copy");

  const handleCopy = async () => {
    try {
      await copyToClipboard(value);
      setCopyLabel("Copied!");
    } catch {
      setCopyLabel("Error");
    } finally {
      setTimeout(() => setCopyLabel("Copy"), 1500);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.5,
        }}
      >
        <CustomLabel
          text={label}
          fontWeight={600}
          fontSize="0.9rem"
          sx={{ margin: 0 }}
        />
        <Tooltip title={copyLabel}>
          <IconButton
            size="small"
            onClick={handleCopy}
            aria-label={`Copy ${label}`}
          >
            <ContentCopyIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography
        component="pre"
        sx={{
          m: 0,
          p: 1.5,
          fontFamily: "monospace",
          fontSize: "0.8rem",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          backgroundColor: "#0d1117",
          color: "#c9d1d9",
          border: "1px solid #30363d",
          borderRadius: "8px",
          maxHeight: "220px",
          overflowY: "auto",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const VpnDiagnosticsDialog = forwardRef<VpnDiagnosticsDialogRef>((_, ref) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<IVpnTunnelDiagnostics | null>(null);
  const [fallbackConnectionName, setFallbackConnectionName] = useState("");

  useImperativeHandle(ref, () => ({
    open: async (id: number, connectionName: string) => {
      setFallbackConnectionName(connectionName);
      setData(null);
      setOpen(true);
      setLoading(true);
      try {
        const result = await fetchVpnTunnelDiagnosticsApi(id);
        setData(result);
      } catch (error: any) {
        ToastNotification.error(error.message);
        setOpen(false);
      } finally {
        setLoading(false);
      }
    },
  }));

  const fields: FieldType[] = loading
    ? [
        {
          type: "custom",
          fullWidth: true,
          component: (
            <Box sx={{ py: 4, textAlign: "center", width: "100%" }}>
              Loading diagnostics...
            </Box>
          ),
        },
      ]
    : data
      ? [
          {
            type: "custom",
            fullWidth: true,
            component: (
              <Box sx={{ fontSize: "0.8rem", color: COLORS.typographyColor }}>
                {data.ipsecVersion}
              </Box>
            ),
          },
          ...(data.errorMessage
            ? [
                {
                  type: "custom" as const,
                  fullWidth: true,
                  component: (
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: "8px",
                        backgroundColor: "#FDECEA",
                        color: "#611A15",
                        fontSize: "0.85rem",
                      }}
                    >
                      {data.errorMessage}
                    </Box>
                  ),
                },
              ]
            : []),
          {
            type: "custom",
            fullWidth: true,
            component: (
              <DiagnosticsPanel label="Config File" value={data.configFile} />
            ),
          },
          {
            type: "custom",
            fullWidth: true,
            component: (
              <DiagnosticsPanel
                label="Status Output"
                value={data.statusOutput}
              />
            ),
          },
          {
            type: "custom",
            fullWidth: true,
            component: (
              <DiagnosticsPanel
                label="Traffic Status"
                value={data.trafficStatus}
              />
            ),
          },
        ]
      : [];

  return (
    <CustomDialog
      open={open}
      onClose={() => setOpen(false)}
      mode="diagnostics"
      title={`VPN Diagnostics - ${data?.connectionName || fallbackConnectionName}`}
      fields={fields}
      buttons={[]}
    />
  );
});

VpnDiagnosticsDialog.displayName = "VpnDiagnosticsDialog";

export default VpnTunnelPage;
